import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Newspaper, ExternalLink } from "lucide-react";

/**
 * NewsTicker — horizontal marquee strip showing the latest job-market news.
 * Pulls from /api/news/feed (RSS aggregation) and merges Editor's Picks on top.
 * Designed for the ProMo main page header — slim, monospaced, low-chrome.
 *
 * Animation runs in CSS (defined inline so the component is drop-in self-contained).
 */
export default function NewsTicker({ className = "" }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [feedRes, picksRes] = await Promise.all([
          api.get("/news/feed"),
          api.get("/news/picks").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const picks = (picksRes.data || []).map((p) => ({ ...p, _pinned: true }));
        const live = feedRes.data?.items || [];
        // Cap total to keep the marquee readable; picks first.
        setItems([...picks, ...live].slice(0, 18));
      } catch {
        /* silently fail — ticker is non-critical chrome */
      }
    };
    load();
    // Refresh every 5 minutes
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (items.length === 0) return null;

  // Duplicate the list so the marquee loops seamlessly.
  const loop = [...items, ...items];

  return (
    <div
      className={`relative overflow-hidden border-y-2 ${className}`}
      style={{
        background: "#0F1B2D",
        color: "white",
        borderColor: "#FFC145",
      }}
      data-testid="news-ticker"
    >
      {/* Scoped marquee animation — kept inline so it travels with the
          component and doesn't pollute global CSS. */}
      <style>{`
        @keyframes resmo-ticker-scroll {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        .resmo-ticker-track {
          animation: resmo-ticker-scroll 90s linear infinite;
          will-change: transform;
        }
        .resmo-ticker-wrap:hover .resmo-ticker-track {
          animation-play-state: paused;
        }
      `}</style>

      <div className="resmo-ticker-wrap flex items-center">
        {/* Left label */}
        <div
          className="flex items-center gap-2 px-3 py-2 font-mono-rm uppercase tracking-widest text-xs flex-shrink-0"
          style={{
            background: "linear-gradient(180deg, #8C3A00 0%, #FF6A00 100%)",
            color: "white",
            borderRight: "2px solid #FFC145",
          }}
          data-testid="news-ticker-label"
        >
          <Newspaper size={14} />
          <span className="hidden sm:inline">Job Market Pulse</span>
          <span className="sm:hidden">Pulse</span>
        </div>

        {/* Scrolling track */}
        <div className="flex-1 overflow-hidden">
          <div
            className="resmo-ticker-track flex items-center whitespace-nowrap"
            style={{ width: "max-content" }}
          >
            {loop.map((item, i) => (
              <a
                key={`${item.url}-${i}`}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium transition-colors"
                style={{
                  color: item._pinned ? "#FFC145" : "#E2E8F0",
                  borderRight: "1px dashed rgba(255,193,69,0.25)",
                }}
                data-testid={`news-ticker-item-${i % items.length}`}
              >
                {item._pinned && (
                  <span
                    className="font-mono-rm text-[10px] uppercase tracking-widest px-1.5 py-0.5"
                    style={{
                      background: "#FFC145",
                      color: "#0F1B2D",
                      borderRadius: 2,
                    }}
                  >
                    Pick
                  </span>
                )}
                <span style={{ color: "#FF6A00" }}>▸</span>
                <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
                  {item.source}
                </span>
                <span>{item.title}</span>
                <ExternalLink size={11} className="opacity-60" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
